/**
 * Componente de filtros para el listado de recogidas
 * Mismo formato de inputs y comportamiento que FiltrosEnvios
 */
import React, { useState, useMemo } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Button,
    Stack,
    Typography,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Badge,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RECOGIDA_ESTADO, RECOGIDA_ESTADO_LABELS } from "../../constants";
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX, ALTURA_BOTON } from "../../../../components/ui/buttonStyles";

/**
 * @param {Function} onFiltrar - Aplicar filtros
 * @param {Function} onLimpiar - Restablecer filtros
 * @param {boolean} loading - Búsqueda en curso
 * @param {number} totalResultados - Resultados tras filtrar, para el contador
 */
export default function FiltrosRecogidas({
    fechaInicio,
    setFechaInicio,
    fechaFin,
    setFechaFin,
    estado,
    setEstado,
    q,
    setQ,
    onFiltrar,
    onLimpiar,
    loading = false,
    totalResultados,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const [expanded, setExpanded] = useState(true);

    // Contador de filtros activos, para el badge
    const filtrosActivos = useMemo(() => {
        let count = 0;
        if (fechaInicio) count++;
        if (fechaFin) count++;
        if (estado) count++;
        if (q) count++;
        return count;
    }, [fechaInicio, fechaFin, estado, q]);

    const rangoInvalido = Boolean(fechaFin && fechaInicio && fechaFin < fechaInicio);

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !loading && !rangoInvalido) {
            onFiltrar();
        }
    };

    const estilosInput = {
        "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
        },
    };

    const filtrosContent = (
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "flex-end" }}
            >
                {/* Búsqueda por texto */}
                <Box sx={{ flex: 1.5 }}>
                    <TextField
                        label="Buscar"
                        placeholder="Ticket, cliente, dirección..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyPress={handleKeyPress}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#6c757d" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={estilosInput}
                    />
                </Box>

                {/* Fecha inicio */}
                <Box sx={{ flex: 1 }}>
                    <TextField
                        type="date"
                        label="Fecha inicio"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        onKeyPress={handleKeyPress}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        sx={estilosInput}
                    />
                </Box>

                {/* Fecha fin */}
                <Box sx={{ flex: 1 }}>
                    <TextField
                        type="date"
                        label="Fecha fin"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        onKeyPress={handleKeyPress}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={rangoInvalido}
                        helperText={rangoInvalido ? "Debe ser posterior a fecha inicio" : ""}
                        sx={estilosInput}
                    />
                </Box>

                {/* Estado */}
                <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Estado</InputLabel>
                        <Select
                            value={estado}
                            onChange={(e) => setEstado(e.target.value)}
                            label="Estado"
                            // Sin displayEmpty, MUI trata el value "" como "sin selección" y
                            // deja el campo en blanco en vez de mostrar la opción "Todos"
                            displayEmpty
                            notched
                            sx={{ backgroundColor: "#fff" }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {Object.values(RECOGIDA_ESTADO).map((key) => (
                                <MenuItem key={key} value={key}>
                                    {RECOGIDA_ESTADO_LABELS[key]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Botón Filtrar */}
                <Box sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { md: 120 } }}>
                    <Button
                        variant="contained"
                        onClick={onFiltrar}
                        disabled={loading || rangoInvalido}
                        fullWidth
                        startIcon={
                            loading ? (
                                <CircularProgress size={18} sx={{ color: "#fff" }} />
                            ) : (
                                <Badge badgeContent={filtrosActivos} color="error">
                                    <FilterListIcon />
                                </Badge>
                            )
                        }
                        sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                    >
                        Filtrar
                    </Button>
                </Box>

                {/* Botón Limpiar */}
                <Box sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { md: 120 } }}>
                    <Button
                        variant="outlined"
                        onClick={onLimpiar}
                        disabled={loading}
                        fullWidth
                        startIcon={<ClearIcon />}
                        sx={{ ...BOTON_SECUNDARIO_SX, height: ALTURA_BOTON.FILTRO }}
                    >
                        Limpiar
                    </Button>
                </Box>
            </Stack>
    );

    const contador = totalResultados !== undefined && (
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, textAlign: isMobile ? "center" : "right" }}
        >
            {totalResultados} {totalResultados === 1 ? "resultado" : "resultados"}
        </Typography>
    );

    // En móvil los filtros ocupan casi toda la pantalla, así que van en un acordeón
    if (isMobile) {
        return (
            <Box sx={{ mb: 3 }}>
                <Accordion
                    expanded={expanded}
                    onChange={() => setExpanded(!expanded)}
                    sx={{
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        borderRadius: "8px !important",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: expanded ? "8px 8px 0 0" : "8px",
                            "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                gap: 1,
                            },
                        }}
                    >
                        <FilterListIcon sx={{ color: "#cc071e" }} />
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>Filtros</Typography>
                        {filtrosActivos > 0 && (
                            <Badge badgeContent={filtrosActivos} color="error" sx={{ mr: 2 }} />
                        )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 3 }}>
                        {filtrosContent}
                        {contador}
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            {filtrosContent}
            {contador}
        </Box>
    );
}
